import React, {
  useCallback, useContext, useEffect, useMemo,
} from 'react';
import uuid from 'react-uuid';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, useTheme } from '@material-ui/core';
import {
  Receipt as ReceiptIcon,
  MoreHoriz as MoreHorizIcon,
  CheckCircle as CheckCircleIcon,
  CancelOutlined as CancelOutlinedIcon,
  Refresh as RefreshIcon,
  RotateLeft as RotateLeftIcon,
  Delete as DeleteIcon,
  Search as PageviewIcon,
} from '@material-ui/icons';
import SimpleConsultPageTemplate from '../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import useOperationController from '../../../../utils/hooks/store/operation/useOperationController';
import { isAlternativeLoadingStatus } from '../../../../utils/store/store-utils';
import AereoContext from '../../../../contexts/agenciamento/house/HouseContext';
import QCXFinalFormDialog from '../../../../shared-components/dialog/QCXFinalFormDialog';
import QCXFinalTextField from '../../../../shared-components/final-text-field/QCXFinalTextField';
import QCXMoveFocusInside from '../../../../components/focus-lock/QCXMoveFocusInside';

import useFormDialogAsync from '../../../../utils/hooks/form/dialog/useFormDialogAsync';
import {
  SUBMITTING_STATUS,
  DONE_STATUS,
  FAIL_STATUS,
  CLOSED_STATUS,
  OPEN_STATUS,
  isOpenStatus,
} from '../../../../utils/hooks/form/dialog/formDialogUtils';

import { followUpAPI } from '../../../../features/follow-up/followUpAPI';
import { setErrorFeedback } from '../../../../features/feedback/feedbackSlice';
import {
  selectAereos,
  fetchAllAsync,
  selectHouseType,
  resetMode,
  resetModel,
  changeToCreateMode,
  selectOperationHouse,
  setModel,
  setOperationImportacao,
  setOperationExportacao,
  setHouseHawbType,
  setHouseHblType,
} from '../../../../features/house/HouseSlice';
import { getBreadcrumbsOfHouse, getTitleOfHouse } from '../../../../features/house/houseUtils';
import { userHasDeleteRole } from '../../../../components/perfil/perfil-utils';



function createHouseConsultConfig({
  operationValue,
  typeValue,
  servicoProdutoFollowUp,

  basePath,

  breadcrumbModulo,
  selectList = selectAereos,

  buttonNewDescriptionKey = 'com.muralis.qcx.aereo.novoCadastro',
  dialogTitleKey = 'com.muralis.qcx.aereo.novoCadastro',
  dialogContentKey = 'com.muralis.qcx.preencherCampoContinuar',
}) {
  return {
    operationValue,
    typeValue,
    servicoProdutoFollowUp,
    basePath,
    breadcrumbModulo,
    selectList,
    buttonNewDescriptionKey,
    dialogTitleKey,
    dialogContentKey,
  };
}

function HouseConsultPageBase({ authInfo = {}, config }) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const type = useSelector(selectHouseType);
  const operation = useSelector(selectOperationHouse);

  const theme = useTheme();
  const { status } = useContext(AereoContext);

  const houseList = useSelector(config.selectList);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
  }, [dispatch]);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
  }, [dispatch]);

  const fetchAll = useCallback(() => {
    dispatch(fetchAllAsync([{ name: 'operacao', value: operation }, { name: 'tipo', value: type }]));
  }, [dispatch, operation]);

  const getMoedaDescription = (row) => row?.custos?.[0]?.moeda?.description ?? '-';

  useEffect(() => {
    if (config.operationValue === 'EXPORTACAO') {
      dispatch(setOperationExportacao());
    } else {
      dispatch(setOperationImportacao());
    }

    if (config.typeValue === 'HBL') {
      dispatch(setHouseHblType());
    } else if (config.typeValue === 'HAWB') {
      dispatch(setHouseHawbType());
    }
  }, [dispatch, config.operationValue, config.typeValue]);


  const breadcrumbs = useMemo(() => {
    const nomeModulo = typeof config.breadcrumbModulo?.name === 'function'
      ? config.breadcrumbModulo.name(t)
      : config.breadcrumbModulo?.name;

    const linkModulo = config.breadcrumbModulo?.to.replace('hawb', '').replace('hbl', '');

    return [
      {
        link: {
          to: '/',
          name: t('com.muralis.qcx.inicio'),
        },
      },
      {
        link: {
          to: linkModulo,
          name: nomeModulo,
        },
      },
      {
        text: {
          name: getBreadcrumbsOfHouse(type),
        },
      },
    ];
  }, [t, config.breadcrumbModulo, type]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(type);
    // eslint-disable-next-line no-console
    console.log(operation);
  }, [type, operation]);

  useEffect(() => {
    fetchAll();
    resetModes();
    resetModels();
  }, [fetchAll, resetModes, resetModels]);


  const handleClickOnEditButton = useCallback((event, id) => {
    event.stopPropagation();
    history.push(`${config.basePath}/${id}/detalhes`);
  }, [history, config.basePath]);

  const handleActivateInactivate = useCallback((event, id) => {
    event.stopPropagation();
  }, []);

  const isAlternativeLoading = useMemo(() => (
    isAlternativeLoadingStatus(status)
  ), [status]);

  const columns = [
    {
      field: 'processo',
      align: 'center',
      headerName: t('com.muralis.qcx.aereo.table.processo'),
      headerAlign: 'center',
      type: 'string',
      flex: 60,
      valueGetter: ({ row }) => row?.numeroProcesso,
    },
    {
      field: 'cliente',
      headerName: t('com.muralis.qcx.aereo.table.cliente'),
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 60,
      valueGetter: ({ row }) => row?.cliente?.description ?? '-',
    },
    {
      field: 'consignatario',
      headerName: t('com.muralis.qcx.aereo.table.consignatario'),
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 60,
      valueGetter: ({ row }) => row?.consignatario?.description ?? '-',
    },
    {
      field: 'origem',
      headerName: t('com.muralis.qcx.aereo.table.origem'),
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 80,
      valueGetter: ({ row }) => row?.origem?.description ?? '-',
    },
    {
      field: 'destino',
      headerName: t('com.muralis.qcx.aereo.table.destino'),
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 80,
      valueGetter: ({ row }) => row?.destino?.description ?? '-',
    },
    {
      field: 'exportador',
      headerName: 'Exportador',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 80,
      valueGetter: ({ row }) => row?.exportador?.pessoaJuridica?.nome ?? '-',
    },
    {
      field: 'incoterm',
      headerName: 'Incoterm',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 80,
      valueGetter: ({ row }) => row?.incotermVenda?.description ?? '-',
    },
    {
      field: 'ativo',
      headerName: 'Ativo',
      headerAlign: 'center',
      align: 'center',
      type: 'boolean',
      flex: 80,
      valueGetter: ({ row }) => row?.active,
    },
    {
      field: 'actions',
      headerName: t('com.muralis.qcx.acoes.label'),
      headerAlign: 'center',
      align: 'center',
      flex: 80,
      renderCell: ({ row: { id, active } }) => (
        <>
          {userHasDeleteRole(authInfo, 'grupo-despesa-receita') && (
            <IconButton
              key={`stw-active${id}`}
              value={active}
              name={`stw-active-${id}`}
              onClick={(event) => handleActivateInactivate(event, id)}
            >
              <DeleteIcon color="error" />
            </IconButton>
          )}
          <IconButton
            key={`btn-view-${id}`}
            name={`btn-view-${id}`}
            onClick={(event) => handleClickOnEditButton(event, id)}
          >
            <PageviewIcon color="primary" />
          </IconButton>
        </>
      ),
    },
  ];


  const [handleButtonNewClick, formDialogNewStatus, handleFormDialogNewStatus] = useFormDialogAsync(
    async (event) => {
      event.stopPropagation();

      dispatch(changeToCreateMode());

      history.push(`${config.basePath}/novo`);
    },
    [history, dispatch, config.basePath]
  );

  const isFormDialogNewOpen = useMemo(() => isOpenStatus(formDialogNewStatus), [formDialogNewStatus]);

  const handleCloseFormDialogNew = useCallback(
    () => handleFormDialogNewStatus(CLOSED_STATUS),
    [handleFormDialogNewStatus]
  );

  const handleProcessoNewSubmit = useCallback(
    async (values) => {
      try {
        handleFormDialogNewStatus(SUBMITTING_STATUS);

        const response = await followUpAPI.fetchByFilter([
          { name: 'processo', value: values?.numeroProcesso },
          { name: 'servicoProduto', value: config.servicoProdutoFollowUp },
        ]);

        const followUp = response?.data?.[0];

        if (!followUp) {
          dispatch(setErrorFeedback({ message: 'Processo não encontrado.' }));
          handleFormDialogNewStatus(OPEN_STATUS);
          return;
        }

        const clienteId = followUp?.importador?.id;
        const servicoId = followUp?.servico?.id;

        if (!clienteId || !servicoId) {
          dispatch(setErrorFeedback({ message: 'Follow-up sem Cliente ou Serviço configurado.' }));
          handleFormDialogNewStatus(OPEN_STATUS);
          return;
        }

        const referencias = (followUp?.referencias || [])
          .filter(Boolean)
          .map((r) => String(r).trim())
          .filter((r) => r.length > 0);

        const referenciasUnicas = Array.from(new Set(referencias));

        dispatch(
          setModel({
            numeroProcesso: followUp?.numero,
            cliente: { id: clienteId },
            servico: { id: servicoId },
            consignatarioIgualCliente: true,
            consignatario: { id: clienteId },
            referencias: referenciasUnicas.map((referencia) => ({
              id: uuid(),
              referencia,
            })),
          })
        );

        handleFormDialogNewStatus(DONE_STATUS);
      } catch (error) {
        handleFormDialogNewStatus(FAIL_STATUS);
        dispatch(setErrorFeedback({ message: 'Erro ao carregar processo.' }));
        handleFormDialogNewStatus(OPEN_STATUS);
      }
    },
    [handleFormDialogNewStatus, dispatch, config.servicoProdutoFollowUp]
  );

  const handleClickOnButtonNew = useCallback(
    (event) => handleButtonNewClick(event),
    [handleButtonNewClick]
  );

  const templateProps = {
    page: {
      title: getTitleOfHouse(type),
      breadcrumbs,
    },
    control: {
      buttonNew: {
        description: t(config.buttonNewDescriptionKey),
        onClick: handleClickOnButtonNew,
      },
    },
    table: {
      columns,
    },
  };

  return (
    <SimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={houseList ?? []}
      isLoading={isAlternativeLoading}
      buttonNewText={t('com.muralis.qcx.acoes.nova').toUpperCase()}
      multipleFilter
      authInfo={authInfo}
    >
      <QCXFinalFormDialog
        key="form-dialog-processo-house-manual"
        id="form-dialog-processo-house-manual"
        title={t(config.dialogTitleKey)}
        content={t(config.dialogContentKey)}
        dialogActionsProps={{
          button: {
            submit: { description: t('com.muralis.qcx.acoes.continuar'), size: 'small' },
            cancel: { description: t('com.muralis.qcx.acoes.cancelar'), size: 'small' },
          },
        }}
        open={isFormDialogNewOpen}
        initialValues={{}}
        onSubmit={handleProcessoNewSubmit}
        onClose={handleCloseFormDialogNew}
      >
        {() => (
          <QCXMoveFocusInside>
            <QCXFinalTextField
              id="text-field-numero-processo-house"
              key="text-field-numero-processo-house"
              name="numeroProcesso"
              label={t('com.muralis.qcx.numeroProcesso')}
              required
            />
          </QCXMoveFocusInside>
        )}
      </QCXFinalFormDialog>
    </SimpleConsultPageTemplate>
  );
}



const labelModulo = (t, operacao, modal, tipo) => {
  const opLabel = operacao === 'EXPORTACAO'
    ? t('com.muralis.qcx.exportacao.label')
    : t('com.muralis.qcx.importacao.label');

  const modalLabel = modal === 'MARITIMO' ? 'Marítimo' : 'Aéreo';
  const tipoLabel = tipo === 'HBL' ? 'HBL' : 'HAWB';

  return `${opLabel} ${modalLabel} ${tipoLabel}`;
};

// IMPORTAÇÃO AÉREO HAWB
export function ImportacaoAereoHouseConsultPage({ authInfo = {} }) {
  const basePath = '/importacao/agenciamento/aereo/hawb';

  const config = createHouseConsultConfig({
    operationValue: 'IMPORTACAO',
    typeValue: 'HAWB',
    servicoProdutoFollowUp: 'AGENCIAMENTO_IMPORTACAO',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'IMPORTACAO', 'AEREO', 'HAWB'),
    },
    selectList: selectAereos,
  });

  return <HouseConsultPageBase authInfo={authInfo} config={config} />;
}

// IMPORTAÇÃO MARÍTIMO HBL
export function ImportacaoMaritimoHouseConsultPage({ authInfo = {} }) {
  const basePath = '/importacao/agenciamento/maritimo/hbl';

  const config = createHouseConsultConfig({
    operationValue: 'IMPORTACAO',
    typeValue: 'HBL',
    servicoProdutoFollowUp: 'AGENCIAMENTO_IMPORTACAO',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'IMPORTACAO', 'MARITIMO', 'HBL'),
    },
    selectList: selectAereos,
  });

  return <HouseConsultPageBase authInfo={authInfo} config={config} />;
}

// EXPORTAÇÃO AÉREO HAWB
export function ExportacaoAereoHouseConsultPage({ authInfo = {} }) {
  const basePath = '/exportacao/agenciamento/aereo/hawb';

  const config = createHouseConsultConfig({
    operationValue: 'EXPORTACAO',
    typeValue: 'HAWB',
    servicoProdutoFollowUp: 'AGENCIAMENTO_EXPORTACAO',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'EXPORTACAO', 'AEREO', 'HAWB'),
    },
    selectList: selectAereos,
  });

  return <HouseConsultPageBase authInfo={authInfo} config={config} />;
}

// EXPORTAÇÃO MARÍTIMO HBL
export function ExportacaoMaritimoHouseConsultPage({ authInfo = {} }) {
  const basePath = '/exportacao/agenciamento/maritimo/hbl';

  const config = createHouseConsultConfig({
    operationValue: 'EXPORTACAO',
    typeValue: 'HBL',
    servicoProdutoFollowUp: 'AGENCIAMENTO_EXPORTACAO',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'EXPORTACAO', 'MARITIMO', 'HBL'),
    },
    selectList: selectAereos,
  });

  return <HouseConsultPageBase authInfo={authInfo} config={config} />;
}


export default ImportacaoAereoHouseConsultPage;
